/**
 * Route chunk "warming".
 *
 * The app gates rendering of <Router/> behind the masterdata fetch (see App.tsx —
 * `if (fetchingData) return <Loader/>`). Because of that gate the landing route's
 * React.lazy `import()` does not fire until masterdata resolves, so its (large)
 * chunk graph only starts downloading *after* masterdata — serializing several
 * seconds of otherwise-idle network.
 *
 * warmRouteChunks() kicks off that download in parallel with the masterdata request
 * (it's called fire-and-forget at the top of loadInitialData). By the time the gate
 * lifts and the route mounts, React.lazy finds the chunks already cached → instant.
 *
 * IMPORTANT: the import specifiers below MUST exactly match the React.lazy imports in
 * Router.tsx, otherwise webpack emits separate chunks and the warming is wasted.
 */

import { applicationType } from '../globals';
import { getAnalyticsLandingPage, getConsignmentsRoute } from '../routing/utils';
import { LocalStorageKeys, LocalStorageService } from 'src/services/localStorage';

type RouteWarmer = {
  test: (pathname: string) => boolean;
  load: () => Promise<unknown>;
};

// show_*_options live only on masterData — undefined at login, so the login-time write populates
// only ops_login_redirect_to; the consignment warmer self-corrects once loadInitialData rewrites.
export type OrgViewConfig = {
  ops_login_redirect_to?: string;
  show_retail_options?: boolean;
  show_logistics_express_options?: boolean;
  show_ltl_options?: boolean;
  show_ondemand_options?: boolean;
  show_crm?: boolean;
};

// Read the org view-config cached at login (4 view flags + ops_login_redirect_to). Returns null
// when absent/corrupt → callers skip warming and fall back to current (post-masterdata) behavior.
export const getCachedViewConfig = (): OrgViewConfig | null => {
  const rawConfig = LocalStorageService.getRaw(LocalStorageKeys.__ORG_VIEW_CONFIG__);
  if (!rawConfig) return null;
  try {
    return JSON.parse(rawConfig);
  } catch (e) {
    return null;
  }
};

// GenericConsignmentViewResolver lazy-picks a heavy view by viewType. Warm the one the cached org
// flags resolve to (all flags false → logistics → Dashboard; any true → GenericConsignmentView) so
// it downloads in parallel with masterdata instead of after.
const warmConsignmentView = (): Promise<unknown> => {
  const viewConfig = getCachedViewConfig();
  if (!viewConfig) return Promise.resolve();
  const isLogistics = !(viewConfig.show_retail_options || viewConfig.show_ltl_options
    || viewConfig.show_ondemand_options || viewConfig.show_logistics_express_options);
  return isLogistics
    ? import('@components/pages/dashboard/Dashboard')
    : import('@components/pages/GenericConsignment/GenericConsignmentView');
};

// /crm-view/crm renders <CrmConsignmentView viewType='crm'> = Dashboard (Router.tsx), gated by
// isAllowed={(express || retail || ltl) && show_crm}. Replicate that auth gate from the cached flags
const warmCrmDashboard = (): Promise<unknown> => {
  const viewConfig = getCachedViewConfig();
  if (!viewConfig) return Promise.resolve();
  const isAllowed = (viewConfig.show_logistics_express_options || viewConfig.show_retail_options
    || viewConfig.show_ltl_options) && viewConfig.show_crm;
  return isAllowed
    ? import('@components/pages/dashboard/Dashboard')
    : Promise.resolve();
};

// The landing route renders AnalyticsLandingDashboard only when the org-level redirect allows it
// (else <Unauthorized/>). Warm it only in that case, using the same cached config.
const warmLandingDashboard = (): Promise<unknown> => {
  const viewConfig = getCachedViewConfig();
  return viewConfig?.ops_login_redirect_to === 'landingPageNew'
    ? import('@components/pages/AnalyticsLandingDashboard')
    : Promise.resolve();
};

const ROUTE_WARMERS: RouteWarmer[] = [
  {
    // Authed shell, mounted on every page: notification bell + firebase push init (pulls the ~600KB
    // firebase/crypto chunk). Specifiers match App.tsx/Navbar.tsx. SideMenu stays interaction-loaded.
    test: () => true,
    load: () => {
      import('@components/navbar/Notifications').catch(() => undefined);
      return import('./notificationServiceHelper');
    },
  },
  {
    // /details/:referenceNumber renders DetailsPage (Router.tsx)
    test: (pathname) => /^\/(ops\/)?details\//.test(pathname),
    load: () => import('@components/pages/details/DetailsPage'),
  },
  {
    // ops root + /consignments render GenericConsignmentViewResolver; warm the shell and the heavy
    // view the cached org flags resolve to.
    test: (pathname) => applicationType === 'ops'
      && (pathname === '/' || pathname.startsWith(getConsignmentsRoute())),
    load: () => {
      import('@components/pages/GenericConsignment/GenericConsignmentViewResolver').catch(() => undefined);
      return warmConsignmentView();
    },
  },
  {
    // ops landing route (/home) renders AnalyticsLandingDashboard when the org redirect allows it.
    test: (pathname) => applicationType === 'ops'
      && (pathname || '').replace(/\/+$/, '') === getAnalyticsLandingPage(),
    load: warmLandingDashboard,
  },
  {
    // ops /dashboard renders OpsDashboard.
    test: (pathname) => applicationType === 'ops' && /^\/dashboard/i.test(pathname),
    load: () => import('@components/pages/OpsDashboard'),
  },
  {
    // CRM consignment view — only for crm app type.
    test: (pathname) => applicationType === 'crm' && /^\/(|consignments|genericConsignment)/i.test(pathname),
    load: () => import('@components/pages/GenericConsignment/GenericConsignmentViewResolver'),
  },
  {
    // ops /crm-view/crm (CRM-in-Ops) renders the Dashboard view; warm it only when the cached org
    // flags satisfy the route's authorization gate.
    test: (pathname) => /^\/(ops\/)?crm-view\/crm(\/|$)/.test(pathname),
    load: warmCrmDashboard,
  },
];

/**
 * Fire-and-forget: start downloading the chunks for whichever route matches `pathname`.
 * Never throws and never blocks — warming is best-effort.
 */
export const warmRouteChunks = (pathname: string): void => {
  try {
    ROUTE_WARMERS.forEach(({ test, load }) => {
      if (test(pathname)) {
        load().catch(() => undefined);
      }
    });
  } catch (e) {
    /* warming must never break startup */
  }
};
